import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import { Labeled, useGetOne } from 'react-admin';
import { ForceTranscriptionButton } from './ForceTranscribeButton';
import { useLogEffect } from '@/util/useLogEffect';
import { format, formatDistance } from 'date-fns';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import ReportIcon from '@mui/icons-material/Report';
import { useMemo, useState } from 'react';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import TimerIcon from '@mui/icons-material/Timer';
import { RecordingOperationHistory } from './RecordingOperationHistory';
import {
  calculateDateDistance,
  DateIntervalDisplay,
  GenericChipDisplay,
  GenericRelativeDateDisplay,
} from '@/components/GenericDateFunctionField';
import { CopiableField } from '@/components/CopiableIdField';
import { RawDataButton } from '@/components/RawDataButton';
import { formatAudioDuration, formatBytes } from '@/components/util';
import { statusConfig } from '../therapistSessionNotes/RecordingStatusSemaphore';
import { DeadLetterQueueButton } from '@/components/DeadLetterQueueButton';
import { IngestionTrackerButton } from '@/components/IngestionTrackerButton';

export type TherapistRecordingViewProps = {
  recording: any;
};

const TranscriptionModelChip = ({ modelId }: { modelId: string }) => {
  const { data, isLoading } = useGetOne('llmModels', {
    id: modelId,
    meta: { raw: true },
  });

  const label = useMemo(() => {
    const loading = isLoading && !data;
    const providerPart = loading ? '…' : (data?.provider ?? '?');
    const modelIdPart = loading
      ? '…'
      : ((data?.id ?? data?._id ?? modelId) as string);
    return `${providerPart}/${modelIdPart}`;
  }, [data, isLoading, modelId]);

  return (
    <Tooltip title={label}>
      <Chip
        size="small"
        label={label}
        sx={{
          maxWidth: 240,
          height: 22,
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            px: 0.75,
            fontSize: '0.7rem',
          },
        }}
      />
    </Tooltip>
  );
};

export const TherapistRecordingView = ({
  recording,
}: TherapistRecordingViewProps) => {
  useLogEffect('TherapistRecordingView', { recording }, [recording]);

  const [operationHistoryExpanded, setOperationHistoryExpanded] =
    useState(false);

  return (
    <>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="baseline"
        justifyContent="flex-start"
        marginBottom="1rem"
      >
        <Labeled label="Recording Id">
          <CopiableField content={recording.id || recording._id} />
        </Labeled>
        <Labeled marginLeft="1rem" label="Created At">
          <GenericRelativeDateDisplay date={recording.createdAt} />
        </Labeled>
        <Labeled marginLeft="1rem" label="Chunk Index">
          <Typography>{recording.fileIndex}</Typography>
        </Labeled>
        <Labeled marginLeft="1rem" label="Duration (ms)">
          <Typography>
            {formatAudioDuration(recording.duration)}
          </Typography>
        </Labeled>
        <PipelineSpeedItem
          title="Compression"
          dateA={recording.committedAt || recording.createdAt}
          dateB={recording.splittedAt}
          color={statusConfig.validated.color}
        />
        <PipelineSpeedItem
          title="Encryption"
          dateA={recording.splittedAt}
          dateB={recording.uploadedAt}
          color={`${statusConfig.uploaded.color}80`}
        />
        <PipelineSpeedItem
          title="Upload Delay"
          dateA={recording.uploadedAt}
          dateB={recording.transcriptionStartedAt}
          color={statusConfig.uploaded.color}
        />
        {recording.transcriptionTime && (
          <Labeled marginLeft="1rem" label="Transcription">
            <Tooltip title="Transcription">
              <Chip
                sx={{
                  backgroundColor: `${statusConfig.transcribing.color}80`,
                }}
                label={`${(
                  recording.transcriptionTime / 1000
                ).toPrecision(3)}s`}
              />
            </Tooltip>
          </Labeled>
        )}
        <PipelineSpeedItem
          title="Total Pipeline"
          dateA={recording.createdAt}
          dateB={recording.transcribedAt}
          // color={statusConfig.transcribed.color}
        />

        {!!recording.error && (
          <Labeled marginLeft="1rem" label="Errors" color="#f99">
            <Tooltip title={recording.error}>
              <Chip
                icon={<ReportIcon />}
                color="error"
                label={recording.error.slice(0, 100)}
              />
            </Tooltip>
          </Labeled>
        )}
        <Labeled marginLeft="1rem" label="Status">
          {(() => {
            const status = recording.status || 'unknown';
            const config =
              statusConfig[status as keyof typeof statusConfig] ||
              statusConfig.unknown;
            return (
              <Chip
                icon={config.icon}
                label={config.label}
                sx={{
                  backgroundColor: config.color,
                  color: 'white',
                  '& .MuiChip-icon': {
                    color: 'white',
                  },
                }}
              />
            );
          })()}
        </Labeled>
        {!!recording.transcript && (
          <>
            <Labeled marginLeft="1rem" label="Transcript">
              <Typography>
                {recording.wordCount
                  ? `${recording.wordCount} word${
                      recording.wordCount > 1 ? 's' : ''
                    }`
                  : 'emtpy'}
              </Typography>
            </Labeled>
          </>
        )}

        {!!recording.uploadedAudioContentSlices && (
          <Labeled marginLeft="1rem" label="Audio Content">
            <Typography>
              {formatBytes(recording.uploadedAudioContentSlices)}
            </Typography>
          </Labeled>
        )}
        {!recording.transcript &&
          !!recording.uploadedAudioContentSlices && (
            <Labeled marginLeft="1rem" label="Actions">
              <ForceTranscriptionButton
                recordingId={recording._id || recording.id}
              />
            </Labeled>
          )}

        <Labeled marginLeft="1rem" label="DLQ">
          <DeadLetterQueueButton
            documentId={recording.id || recording._id}
            userId={recording.therapistId}
            collectionName="therapistRecordings"
          />
        </Labeled>

        {!!recording.operationHistory?.length && (
          <>
            <Labeled label="Operations" marginLeft="1rem">
              <Box>
                <Chip
                  icon={<FormatListNumberedIcon />}
                  label={recording.operationHistory?.length}
                />
              </Box>
            </Labeled>
            <Labeled label="Expand" marginLeft="0.5rem">
              <IconButton
                sx={{ marginLeft: '.5rem' }}
                onClick={() =>
                  setOperationHistoryExpanded(
                    !operationHistoryExpanded,
                  )
                }
              >
                {operationHistoryExpanded ? (
                  <KeyboardArrowUpIcon />
                ) : (
                  <KeyboardArrowDownIcon />
                )}
              </IconButton>
            </Labeled>
          </>
        )}
        <Labeled marginLeft="1rem" label="Ingestion">
          <IngestionTrackerButton
            ingestionRecords={recording.ingestionTracker}
          />
        </Labeled>
        <Labeled marginLeft="1rem" label="Raw Data">
          <RawDataButton
            entity={recording}
            title="Raw Recording Data"
          />
        </Labeled>
        {!!recording.deletedAt && (
          <Labeled marginLeft="1rem" label="Deleted At">
            <GenericRelativeDateDisplay date={recording.deletedAt} />
          </Labeled>
        )}
        {!!recording.transcriptionModelId && (
          <Box marginLeft="1rem" display="flex" alignItems="center">
            <TranscriptionModelChip
              modelId={recording.transcriptionModelId}
            />
          </Box>
        )}
      </Box>
      {operationHistoryExpanded && (
        <RecordingOperationHistory
          operationHistory={recording.operationHistory}
        />
      )}
    </>
  );
};

export const rowSectionStyle = {
  display: 'flex',
  flexDirection: 'row',
} as const;

export const RecordingHistoryItem = ({ item }: { item: any }) => {
  const when = useMemo(() => {
    try {
      return new Date(item.when);
    } catch (error) {
      return new Date();
    }
  }, [item.when]);

  const isSuccess = item.result === 'success';

  return (
    <Box
      display="flex"
      alignItems="center"
      padding="0.25rem 1rem"
      key={item._id}
    >
      <Tooltip
        title={
          when
            ? formatDistance(when, new Date(), {
                addSuffix: true,
                includeSeconds: true,
              })
            : 'not set ⚠️'
        }
      >
        <Chip
          label={
            when
              ? format(when, 'MM/dd/yyyy HH:mm:ss.SSS')
              : 'not set ⚠️'
          }
        />
      </Tooltip>
      <Chip
        sx={{ marginLeft: '.5rem' }}
        icon={<TimerIcon />}
        label={`${item.duration / 1000}s`}
      />
      <Chip
        sx={{ marginLeft: '.5rem' }}
        icon={isSuccess ? <CheckIcon /> : <ErrorIcon />}
        color={isSuccess ? 'success' : 'error'}
        label={item.operation}
      />
      <RelevantContextTooltip item={item} />
    </Box>
  );
};

const RelevantContextTooltip = ({ item }: { item: any }) => {
  const contextTooltip = useMemo(() => {
    try {
      const { message, ...errorMetadata } = item.error;

      const parsedJSON = JSON.parse(item.relevantContext);

      return JSON.stringify(
        { ...parsedJSON, errorMetadata },
        null,
        2,
      );
    } catch (error) {
      return JSON.stringify(item.relevantContext.trim());
    }
  }, [item.relevantContext, item.error]);

  const errorMessage = useMemo(() => {
    if (item.error?.message) {
      return item.error.message;
    }

    const itemError = item.error?.trim();

    if (itemError) {
      return JSON.stringify(itemError);
    }

    return null;
  }, [item.error]);

  return (
    <Tooltip sx={{ marginLeft: '.5rem' }} title={contextTooltip}>
      <Chip
        color={item.error ? 'error' : 'default'}
        label={errorMessage || 'Hover for context'}
      />
    </Tooltip>
  );
};

type PipelineStatsChipProps = {
  title: string;
  dateA: string;
  dateB: string;
  color?: string;
};

export const PipelineSpeedItem = ({
  title,
  dateA,
  dateB,
  color,
}: PipelineStatsChipProps) => {
  const { dateDistance, date1Date, date2Date } = useMemo(
    () => calculateDateDistance(dateA, dateB),
    [dateA, dateB],
  );

  if (dateDistance === null) {
    return null;
  }

  return (
    <Labeled marginLeft="1rem" label={title}>
      <Tooltip title={title}>
        <DateIntervalDisplay
          date1={dateA}
          date2={dateB}
          chipColor={color}
        />
      </Tooltip>
    </Labeled>
  );
};

export type PipelineSpeedDuration = {
  title: string;
  duration: number;
  color?: string;
};

export const PipelineSpeedDuration = ({
  title,
  duration,
  color,
}: PipelineSpeedDuration) => (
  <Labeled marginLeft="1rem" label={title}>
    <Tooltip title={title}>
      <GenericChipDisplay label={`${duration}s`} chipColor={color} />
    </Tooltip>
  </Labeled>
);
